import { ArrowLeftOutlined, DeleteOutlined, PlusOutlined, ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Result,
  Row,
  Select,
  Space,
  Spin,
  Typography,
  message,
} from "antd";
import debounce from "lodash/debounce";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import {
  createFinalInspection,
  fetchDefectCategories,
  fetchFinalInspectionById,
  fetchFinalInspectionOrderQty,
  fetchFinalInspectionStyles,
  fetchInspectionLeadBys,
  fetchInspectionTypes,
  isRequestCancelled,
  updateFinalInspection,
} from "apiServices/finalProductInspectionService";
import {
  buildFinalInspectionPayload,
  calculateDefectSummary,
  sampleQtyForInspectionQty,
  sumDefectBreakdown,
} from "./buildFinalInspectionPayload";
import { DATE_FORMAT, INSPECTION_STATUS_OPTIONS, LIST_PATH, STYLE_PAGE_SIZE, STYLE_SEARCH_DEBOUNCE_MS } from "./constants";
import useActiveList from "./useActiveList";
import "./finalInspection.scss";

const { Text, Title } = Typography;
const { TextArea } = Input;

/** Laravel field → form field that should display the error. */
const SERVER_FIELD_TO_FORM_FIELD = {
  style_id: "style_id",
  buyer_id: "style_id",
  buying_office_id: "style_id",
  sub_contract_factory_id: "style_id",
  po_map_id: "po_map_ids",
  po_maps: "po_map_ids",
  sub_style_id: "sub_style_id",
  color_id: "color_id",
  report_link: "report_link",
  inspection_date: "inspection_date",
  ship_date: "ship_date",
  ship_qty: "ship_qty",
  inspection_qty: "inspection_qty",
  sample_qty: "sample_qty",
  aql_value: "aql_value",
  inspection_type_id: "inspection_type_id",
  inspection_lead_by_id: "inspection_lead_by_id",
  inspection_status: "inspection_status",
  critical_qty: "critical_qty",
  major_qty: "major_qty",
  minor_qty: "minor_qty",
  note: "note",
};

const mergeById = (base, extra) => {
  const map = new Map();
  [...base, ...extra].forEach((item) => {
    if (item?.id != null) map.set(Number(item.id), item);
  });
  return Array.from(map.values());
};

const getPoList = (style) => style?.active_sales_contract_map_info?.active_sales_contract_info?.po_info || [];

/** Minimal style built from a saved record when the style search doesn't return it. */
const styleFromRecord = (record) => ({
  id: record.style_id,
  style_no: record.style?.style_no || `Style #${record.style_id}`,
  buyer_id: record.buyer_id,
  buying_office_id: record.buying_office_id,
  sub_contract_factory_id: record.sub_contract_factory_id,
  buyer_info: record.buyer || null,
  buying_office_info: record.buying_office || null,
  factory_info: record.sub_contract_factory || null,
  active_sales_contract_map_info: {
    sales_contract_id: record.sales_contract_id,
    active_sales_contract_info: { ...(record.sales_contract || {}), po_info: [] },
  },
  sub_style: [],
  color_map: [],
});

/** Keeps the saved sales contract, PO(s), sub style and color selectable in edit mode. */
const withRecordFallbacks = (style, record) => {
  const contractInfo = style.active_sales_contract_map_info?.active_sales_contract_info || {};
  const poList = getPoList(style);
  const subStyles = style.sub_style || [];
  const colorMap = style.color_map || [];

  const recordPos = [];
  if (Array.isArray(record.po_maps)) {
    record.po_maps.forEach((item) => {
      if (item?.po_map) {
        recordPos.push(item.po_map);
      } else if (item?.po_map_id) {
        recordPos.push({ id: item.po_map_id, original_po: `PO #${item.po_map_id}` });
      }
    });
  }
  if (record.po_map && !recordPos.some((p) => Number(p.id) === Number(record.po_map.id))) {
    recordPos.push(record.po_map);
  }

  const mergedPoList = [...poList];
  recordPos.forEach((recPo) => {
    if (!mergedPoList.some((po) => Number(po.id) === Number(recPo.id))) {
      mergedPoList.push(recPo);
    }
  });

  return {
    ...style,
    active_sales_contract_map_info: {
      ...(style.active_sales_contract_map_info || {}),
      sales_contract_id: record.sales_contract_id,
      active_sales_contract_info: {
        ...(record.sales_contract || {}),
        ...contractInfo,
        po_info: mergedPoList,
      },
    },
    sub_style:
      record.sub_style && !subStyles.some((s) => Number(s.id) === Number(record.sub_style.id))
        ? [...subStyles, record.sub_style]
        : subStyles,
    color_map:
      record.color && !colorMap.some((c) => Number(c.color_id) === Number(record.color.id))
        ? [...colorMap, { id: 0, style_id: record.style_id, color_id: record.color.id, color_info: record.color }]
        : colorMap,
  };
};

const urlRule = {
  validator: (_, value) => {
    if (!value) return Promise.resolve();
    return /^https?:\/\/\S+$/i.test(String(value).trim())
      ? Promise.resolve()
      : Promise.reject(new Error("Enter a valid link starting with http:// or https://"));
  },
};

const FinalInspectionForm = ({ isEdit }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const editId = isEdit ? Number(id) : null;
  const mode = editId ? "edit" : "create";

  const [inspectionTypes] = useActiveList(fetchInspectionTypes, "Failed to load inspection types.");
  const [leadBys] = useActiveList(fetchInspectionLeadBys, "Failed to load inspection lead by list.");
  const [defectCategories, defectCategoriesLoading] = useActiveList(
    fetchDefectCategories,
    "Failed to load defect categories.",
  );

  const goToList = () => navigate(LIST_PATH);

  const [styleOptions, setStyleOptions] = useState([]);
  const [styleSearch, setStyleSearch] = useState("");
  const [stylePaging, setStylePaging] = useState({ current: 0, last: 1 });
  const [styleLoading, setStyleLoading] = useState(false);
  const styleRequestRef = useRef(0);
  const [selectedStyle, setSelectedStyle] = useState(null);

  const [orderQty, setOrderQty] = useState(null);
  const [orderQtyLoading, setOrderQtyLoading] = useState(false);

  const [record, setRecord] = useState(null);
  const [recordLoading, setRecordLoading] = useState(mode === "edit");
  const [recordError, setRecordError] = useState(null);
  const [recordReloadKey, setRecordReloadKey] = useState(0);

  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [generalErrors, setGeneralErrors] = useState([]);

  const poMapIds = Form.useWatch("po_map_ids", form);
  const subStyleId = Form.useWatch("sub_style_id", form);
  const colorId = Form.useWatch("color_id", form);
  const criticalQty = Form.useWatch("critical_qty", form);
  const majorQty = Form.useWatch("major_qty", form);
  const minorQty = Form.useWatch("minor_qty", form);
  const inspectionQty = Form.useWatch("inspection_qty", form);
  const aqlValue = Form.useWatch("aql_value", form);
  const sampleQty = Form.useWatch("sample_qty", form);
  const defects = Form.useWatch("defects", form);

  useEffect(() => {
    const hasInspectionQty = inspectionQty !== null && inspectionQty !== undefined && inspectionQty !== "";
    const hasAql = aqlValue !== null && aqlValue !== undefined && aqlValue !== "";
    const nextSampleQty = hasInspectionQty && hasAql ? sampleQtyForInspectionQty(inspectionQty) : null;
    if (form.getFieldValue("sample_qty") !== nextSampleQty) {
      form.setFieldsValue({ sample_qty: nextSampleQty });
    }
  }, [inspectionQty, aqlValue, form]);

  /* --------------------------------- Styles --------------------------------- */

  const loadStyles = useCallback(async (search, page) => {
    const requestId = ++styleRequestRef.current;
    setStyleLoading(true);
    try {
      const result = await fetchFinalInspectionStyles({ search, page, perPage: STYLE_PAGE_SIZE });
      if (requestId !== styleRequestRef.current) return;
      setStyleOptions((prev) => (page === 1 ? result.items : mergeById(prev, result.items)));
      setStylePaging({ current: result.currentPage, last: result.lastPage });
    } catch (error) {
      if (requestId === styleRequestRef.current) message.error(error?.message || "Failed to load styles.");
    } finally {
      if (requestId === styleRequestRef.current) setStyleLoading(false);
    }
  }, []);

  const debouncedStyleSearch = useMemo(
    () =>
      debounce((value) => {
        setStyleSearch(value);
        loadStyles(value, 1);
      }, STYLE_SEARCH_DEBOUNCE_MS),
    [loadStyles],
  );

  useEffect(() => {
    loadStyles("", 1);
    return () => debouncedStyleSearch.cancel();
  }, [loadStyles, debouncedStyleSearch]);

  const handleStylePopupScroll = (event) => {
    const target = event.target;
    const nearBottom = target.scrollTop + target.offsetHeight >= target.scrollHeight - 24;
    if (nearBottom && !styleLoading && stylePaging.current < stylePaging.last) {
      loadStyles(styleSearch, stylePaging.current + 1);
    }
  };

  const visibleStyleOptions = useMemo(
    () => (selectedStyle ? mergeById([selectedStyle], styleOptions) : styleOptions),
    [selectedStyle, styleOptions],
  );

  const handleStyleChange = (styleId) => {
    const style = visibleStyleOptions.find((s) => Number(s.id) === Number(styleId)) || null;
    setSelectedStyle(style);
    const poList = getPoList(style);
    const subStyles = style?.sub_style || [];
    const colors = style?.color_map || [];
    form.setFieldsValue({
      po_map_ids: poList.length === 1 ? [Number(poList[0].id)] : [],
      sub_style_id: subStyles.length === 1 ? Number(subStyles[0].id) : undefined,
      color_id: colors.length === 1 ? Number(colors[0].color_id) : undefined,
    });
  };

  /* -------------------------------- Order qty -------------------------------- */

  const styleId = selectedStyle?.id ?? null;

  useEffect(() => {
    if (!styleId) {
      setOrderQty(null);
      return undefined;
    }
    const controller = new AbortController();
    setOrderQtyLoading(true);
    fetchFinalInspectionOrderQty({ styleId, poMapIds, subStyleId, colorId }, controller.signal)
      .then(setOrderQty)
      .catch((error) => {
        if (!isRequestCancelled(error)) setOrderQty(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setOrderQtyLoading(false);
      });
    return () => controller.abort();
  }, [styleId, poMapIds, subStyleId, colorId]);

  /* ------------------------------- Edit hydration ------------------------------ */

  useEffect(() => {
    if (mode !== "edit") return undefined;
    const controller = new AbortController();
    setRecordLoading(true);
    setRecordError(null);

    (async () => {
      try {
        const rec = await fetchFinalInspectionById(editId, controller.signal);
        let style = null;
        if (rec.style?.style_no) {
          try {
            const result = await fetchFinalInspectionStyles(
              { search: rec.style.style_no, page: 1, perPage: STYLE_PAGE_SIZE },
              controller.signal,
            );
            style = result.items.find((s) => Number(s.id) === Number(rec.style_id)) || null;
          } catch (error) {
            if (isRequestCancelled(error)) throw error;
          }
        }
        style = style ? withRecordFallbacks(style, rec) : withRecordFallbacks(styleFromRecord(rec), rec);

        const toOptionalNumber = (value) => (value === null || value === undefined ? undefined : Number(value));

        const savedPoMapIds = Array.isArray(rec.po_maps) && rec.po_maps.length > 0
          ? rec.po_maps.map((p) => Number(p.po_map_id || p.po_map?.id)).filter(Boolean)
          : rec.po_map_id
          ? [Number(rec.po_map_id)]
          : [];

        setRecord(rec);
        setSelectedStyle(style);
        form.setFieldsValue({
          style_id: Number(style.id),
          po_map_ids: savedPoMapIds,
          sub_style_id: toOptionalNumber(rec.sub_style_id),
          color_id: toOptionalNumber(rec.color_id),
          report_link: rec.report_link || "",
          inspection_date: rec.inspection_date ? moment(rec.inspection_date) : undefined,
          ship_date: rec.ship_date ? moment(rec.ship_date) : undefined,
          ship_qty: toOptionalNumber(rec.ship_qty),
          inspection_qty: toOptionalNumber(rec.inspection_qty),
          sample_qty: toOptionalNumber(rec.sample_qty),
          aql_value: toOptionalNumber(rec.aql_value),
          inspection_type_id: toOptionalNumber(rec.inspection_type_id),
          inspection_lead_by_id: toOptionalNumber(rec.inspection_lead_by_id),
          inspection_status: rec.inspection_status
            ? rec.inspection_status.charAt(0).toUpperCase() + rec.inspection_status.slice(1).toLowerCase()
            : undefined,
          critical_qty: toOptionalNumber(rec.critical_qty),
          major_qty: toOptionalNumber(rec.major_qty),
          minor_qty: toOptionalNumber(rec.minor_qty),
          note: rec.note || "",
          defects: (rec.details || []).map((detail) => ({
            id: detail.id,
            defect_category_id: Number(detail.defect_category_id),
            defect_value: Number(detail.defect_value),
          })),
        });
      } catch (error) {
        if (!isRequestCancelled(error)) setRecordError(error?.message || "Failed to load final inspection.");
      } finally {
        if (!controller.signal.aborted) setRecordLoading(false);
      }
    })();

    return () => controller.abort();
  }, [mode, editId, recordReloadKey, form]);

  /* ---------------------------------- Summary ---------------------------------- */

  const { totalDefectQty, oqlValue } = calculateDefectSummary({ criticalQty, majorQty, minorQty, inspectionQty });
  const breakdownTotal = sumDefectBreakdown(defects);
  const breakdownMismatch = (defects || []).some((row) => row?.defect_category_id) && breakdownTotal !== totalDefectQty;

  const missingSalesContract = !!selectedStyle && !selectedStyle.active_sales_contract_map_info?.sales_contract_id;
  const canSubmit = !!selectedStyle && !missingSalesContract && !recordLoading && !recordError && !saving;

  /* ---------------------------------- Submit ---------------------------------- */

  const applyServerErrors = (fieldErrors) => {
    const byFormField = {};
    const unmapped = [];
    Object.entries(fieldErrors || {}).forEach(([serverField, messages]) => {
      const list = Array.isArray(messages) ? messages : [String(messages)];
      let formField = SERVER_FIELD_TO_FORM_FIELD[serverField];
      if (!formField && serverField.startsWith("po_maps")) {
        formField = "po_map_ids";
      }
      if (formField) {
        byFormField[formField] = [...(byFormField[formField] || []), ...list];
      } else {
        unmapped.push(...list);
      }
    });
    form.setFields(Object.entries(byFormField).map(([name, errors]) => ({ name, errors })));
    setGeneralErrors(Array.from(new Set(unmapped)));
  };

  const handleSubmit = async () => {
    if (savingRef.current || !canSubmit) return;
    try {
      await form.validateFields();
    } catch {
      return;
    }

    const values = form.getFieldsValue(true);
    const payload = buildFinalInspectionPayload(
      values,
      selectedStyle,
      mode === "edit" ? record?.details : [],
      mode === "edit" ? record?.po_maps : [],
    );

    savingRef.current = true;
    setSaving(true);
    setGeneralErrors([]);
    const result = mode === "edit" ? await updateFinalInspection(editId, payload) : await createFinalInspection(payload);
    savingRef.current = false;
    setSaving(false);

    if (result.ok) {
      message.success(result.message || "Final inspection saved.");
      goToList();
      return;
    }
    message.error(result.message);
    applyServerErrors(result.fieldErrors);
  };

  const handleValuesChange = (changed) => {
    const names = Object.keys(changed).filter((name) => name !== "defects");
    if (names.length) form.setFields(names.map((name) => ({ name, errors: [] })));
    if (generalErrors.length) setGeneralErrors([]);
  };

  /* ---------------------------------- Render ---------------------------------- */

  const poOptions = useMemo(
    () =>
      getPoList(selectedStyle).map((po) => ({
        value: Number(po.id),
        label: po.original_po || po.internal_po || `PO #${po.id}`,
      })),
    [selectedStyle],
  );

  const subStyleOptions = useMemo(
    () =>
      (selectedStyle?.sub_style || []).map((s) => ({
        value: Number(s.id),
        label: s.sub_style_no || `Sub Style #${s.id}`,
      })),
    [selectedStyle],
  );

  const colorOptions = useMemo(() => {
    const seen = new Set();
    return (selectedStyle?.color_map || [])
      .filter((c) => {
        if (!c.color_id || seen.has(Number(c.color_id))) return false;
        seen.add(Number(c.color_id));
        return true;
      })
      .map((c) => ({ value: Number(c.color_id), label: c.color_info?.name || `Color #${c.color_id}` }));
  }, [selectedStyle]);

  const defectCategoryOptions = useMemo(
    () => defectCategories.map((category) => ({ value: category.id, label: category.name })),
    [defectCategories],
  );

  const salesContract = selectedStyle?.active_sales_contract_map_info?.active_sales_contract_info;
  const editPending = mode === "edit" && recordLoading;

  const renderForm = () => (
    <Spin spinning={editPending} tip="Loading inspection..." size="large">
      <Form
        form={form}
        layout="vertical"
        size="small"
        onValuesChange={handleValuesChange}
        initialValues={{ defects: [] }}
        requiredMark
      >
        <Card size="small" title="Style & Order" className="fpi-card">
          <Row gutter={12}>
            <Col xs={24} md={6}>
              <Form.Item
                label="Style"
                name="style_id"
                rules={[{ required: true, message: "Style is required" }]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Search style no."
                  filterOption={false}
                  onSearch={debouncedStyleSearch}
                  onChange={handleStyleChange}
                  onPopupScroll={handleStylePopupScroll}
                  loading={styleLoading}
                  notFoundContent={styleLoading ? <Spin size="small" /> : "No styles found"}
                  options={visibleStyleOptions.map((s) => ({ value: Number(s.id), label: s.style_no }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label={selectedStyle?.sub_contract_factory_id ? "Sub Contract Factory" : "Buyer"}>
                <Input
                  disabled
                  placeholder="Auto-filled from style"
                  value={
                    selectedStyle?.sub_contract_factory_id
                      ? selectedStyle?.factory_info?.name || ""
                      : selectedStyle?.buyer_info?.name || ""
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Buying Office">
                <Input disabled placeholder="Auto-filled from style" value={selectedStyle?.buying_office_info?.name || ""} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                label="Sales Contract"
                required
                validateStatus={missingSalesContract ? "error" : undefined}
                help={missingSalesContract ? "This style has no active sales contract. Sales contract is required." : undefined}
              >
                <Input
                  disabled
                  placeholder="Auto-filled from style"
                  value={salesContract?.reference_no || salesContract?.sales_contract_unique_id || ""}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={12} md={6}>
              <Form.Item label="PO" name="po_map_ids">
                <Select
                  mode="multiple"
                  showSearch
                  allowClear
                  maxTagCount="responsive"
                  optionFilterProp="label"
                  placeholder={selectedStyle ? "Select PO(s)" : "Select a style first"}
                  disabled={!selectedStyle}
                  options={poOptions}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label="Sub Style" name="sub_style_id">
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  placeholder={selectedStyle ? "Select sub style" : "Select a style first"}
                  disabled={!selectedStyle}
                  options={subStyleOptions}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label="Color" name="color_id">
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  placeholder={selectedStyle ? "Select color" : "Select a style first"}
                  disabled={!selectedStyle}
                  options={colorOptions}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label="Order Qty">
                <Input
                  disabled
                  placeholder="Auto-calculated"
                  value={orderQtyLoading ? "Loading..." : orderQty ?? ""}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card size="small" title="Inspection" className="fpi-card">
          <Row gutter={12}>
            <Col xs={12} md={6}>
              <Form.Item
                label="Inspection Date"
                name="inspection_date"
                rules={[{ required: true, message: "Inspection date is required" }]}
              >
                <DatePicker style={{ width: "100%" }} format={DATE_FORMAT} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label="Inspection Type" name="inspection_type_id">
                <Select
                  allowClear
                  placeholder="Select type"
                  options={inspectionTypes.map((type) => ({ value: Number(type.id), label: type.name }))}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label="Inspection Lead By" name="inspection_lead_by_id">
                <Select
                  allowClear
                  placeholder="Select lead by"
                  options={leadBys.map((leadBy) => ({ value: Number(leadBy.id), label: leadBy.name }))}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                label="Inspection Status"
                name="inspection_status"
                rules={[{ required: true, message: "Inspection status is required" }]}
              >
                <Select placeholder="Select status" options={INSPECTION_STATUS_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={12} md={6}>
              <Form.Item label="Ship Date" name="ship_date">
                <DatePicker style={{ width: "100%" }} format={DATE_FORMAT} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label="Ship Qty" name="ship_qty">
                <InputNumber style={{ width: "100%" }} min={0} precision={0} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label="Inspection Qty" name="inspection_qty">
                <InputNumber style={{ width: "100%" }} min={0} precision={0} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label="AQL" name="aql_value">
                <Select
                  allowClear
                  placeholder="Select AQL"
                  options={[
                    { value: 1, label: "1.0" },
                    { value: 1.5, label: "1.5" },
                    { value: 2.5, label: "2.5" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={12} md={6}>
              <Form.Item label="Sample Qty" name="sample_qty">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  precision={0}
                  disabled
                  placeholder="From inspection qty"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Report Link"
                name="report_link"
                rules={[{ required: true, message: "Report link is required" }, urlRule]}
              >
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label="Note" name="note">
                <TextArea rows={1} autoSize={{ minRows: 1, maxRows: 3 }} placeholder="Optional" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card size="small" title="Defects" className="fpi-card">
          <Row gutter={12}>
            <Col xs={8} md={6}>
              <Form.Item label="Critical Qty" name="critical_qty">
                <InputNumber style={{ width: "100%" }} min={0} precision={0} />
              </Form.Item>
            </Col>
            <Col xs={8} md={6}>
              <Form.Item label="Major Qty" name="major_qty">
                <InputNumber style={{ width: "100%" }} min={0} precision={0} />
              </Form.Item>
            </Col>
            <Col xs={8} md={6}>
              <Form.Item label="Minor Qty" name="minor_qty">
                <InputNumber style={{ width: "100%" }} min={0} precision={0} />
              </Form.Item>
            </Col>
          </Row>

          <Text strong className="fpi-subtitle">
            Defect Breakdown
          </Text>
          <Form.List name="defects">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Row gutter={12} key={field.key} align="top">
                    <Form.Item name={[field.name, "id"]} hidden>
                      <Input />
                    </Form.Item>
                    <Col xs={14} md={10}>
                      <Form.Item
                        name={[field.name, "defect_category_id"]}
                        rules={[{ required: true, message: "Defect category is required" }]}
                      >
                        <Select
                          showSearch
                          optionFilterProp="label"
                          placeholder="Select defect category"
                          loading={defectCategoriesLoading}
                          options={defectCategoryOptions}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={7} md={6}>
                      <Form.Item
                        name={[field.name, "defect_value"]}
                        rules={[{ required: true, message: "Defect qty is required" }]}
                      >
                        <InputNumber style={{ width: "100%" }} min={0} precision={0} placeholder="Qty" />
                      </Form.Item>
                    </Col>
                    <Col xs={3} md={2}>
                      <Button danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ id: 0 })}>
                  Add Defect
                </Button>
              </>
            )}
          </Form.List>

          {generalErrors.length > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginTop: 12 }}
              message="Please fix the following"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {generalErrors.map((err) => (
                    <li key={err}>{err}</li>
                  ))}
                </ul>
              }
            />
          )}

          <Row className="fpi-summary" gutter={24} align="middle">
            <Col>
              <Text strong>Total Defect Qty:</Text> {totalDefectQty}
            </Col>
            <Col>
              <Text strong>OQL:</Text> {oqlValue}%
            </Col>
            {breakdownMismatch && (
              <Col>
                <Text type="warning">
                  Defect breakdown total ({breakdownTotal}) doesn't match Critical + Major + Minor ({totalDefectQty}).
                </Text>
              </Col>
            )}
          </Row>
        </Card>
      </Form>
    </Spin>
  );

  return (
    <AppContent
      header={
        <AppPageHeader
          title={
            <Title level={5} style={{ margin: 0 }}>
              {mode === "edit" ? "Edit Final Inspection" : "Add Final Inspection"}
            </Title>
          }
          extra={
            <Space>
              <Button size="small" icon={<ArrowLeftOutlined />} onClick={goToList} disabled={saving}>
                Back
              </Button>
              <Button
                size="small"
                type="primary"
                icon={<SaveOutlined />}
                loading={saving}
                disabled={!canSubmit}
                onClick={handleSubmit}
              >
                {mode === "edit" ? "Update" : "Save"}
              </Button>
            </Space>
          }
        />
      }
      content={
        <div className="final-inspection-form">
          {recordError ? (
            <Result
              status="error"
              title="Could not load this inspection"
              subTitle={recordError}
              extra={
                <Space>
                  <Button onClick={goToList}>Back to list</Button>
                  <Button type="primary" icon={<ReloadOutlined />} onClick={() => setRecordReloadKey((k) => k + 1)}>
                    Retry
                  </Button>
                </Space>
              }
            />
          ) : (
            renderForm()
          )}
        </div>
      }
    />
  );
};

export default FinalInspectionForm;
